import {
  type CoachingRequester,
  ensureLiveOrgId,
  operationContract,
  requestContract,
  requiresLiveOrgId,
  withLiveOrgId
} from "./coaching-services";
import { buildApiPath } from "./api-contracts";
import { getCoachingOrgId } from "./coaching-org-context";
import { coachingEndpoints } from "./coaching-contracts";
import {
  liveOrgAnalyticsSummary,
  liveOrgAnalyticsTimeSeries,
  liveOrgReportStatus
} from "./coaching-live-readmodels";
import type {
  CoachingOrgAnalyticsSummary,
  CoachingOrgAnalyticsTimeSeries,
  CoachingOrgReportStatus,
  ReportReviewRequest
} from "./coaching-dtos";

type Query = Record<string, string | number | boolean | null | undefined>;

/**
 * text/csv export transport. Feature-detects `requestText` (live) and falls
 * back to the JSON acknowledgement (mock); injects orgId exactly like the JSON
 * path so the export stays company-scoped.
 */
async function requestContractText(
  requester: CoachingRequester,
  exportType: string,
  query?: Query
): Promise<string> {
  const contract = coachingEndpoints["exports.csv"];
  const path = buildApiPath(contract.pathTemplate, { export_type: exportType }, withLiveOrgId(contract, query));
  if (typeof requester.requestText === "function") {
    if (requiresLiveOrgId(contract) && !getCoachingOrgId()) await ensureLiveOrgId(requester);
    return requester.requestText(buildApiPath(contract.pathTemplate, { export_type: exportType }, withLiveOrgId(contract, query)));
  }
  const payload = await requester.request<unknown>(path, { method: "GET" });
  return typeof payload === "string" ? payload : JSON.stringify(payload ?? { ok: true, export_type: exportType });
}

/**
 * Phase 4 org analytics rollup family (analytics spec §4.4). Each rollup is a
 * record (not a list) with its own envelope unwrapper, so they bypass the
 * family-keyed mapper lookup. Routes are ungated/zero-filled-200; a transient
 * failure surfaces normally and the hook treats it as the honest empty state
 * (never a 503 toast).
 */
export function analyticsServices(requester: CoachingRequester) {
  return {
    orgSummary: async (query?: Query): Promise<CoachingOrgAnalyticsSummary> =>
      liveOrgAnalyticsSummary((await requestContract<unknown>(requester, "analytics.orgSummary", { query })) as Record<string, unknown>),
    timeSeries: async (query?: Query): Promise<CoachingOrgAnalyticsTimeSeries> =>
      liveOrgAnalyticsTimeSeries((await requestContract<unknown>(requester, "analytics.timeSeries", { query })) as Record<string, unknown>),
    reportStatus: async (query?: Query): Promise<CoachingOrgReportStatus> =>
      liveOrgReportStatus((await requestContract<unknown>(requester, "analytics.reportStatus", { query })) as Record<string, unknown>)
  };
}

/**
 * Phase 4 report follow-through: the mark-reviewed write-back + the CSV export.
 * Spread into the `reports` service family.
 */
export function reportFollowThroughServices(requester: CoachingRequester) {
  return {
    markReviewed: (reportId: string, body: ReportReviewRequest, idempotencyKey: string) =>
      operationContract(requester, "reports.markReviewed", {
        pathParams: { report_id: reportId },
        body,
        idempotencyKey
      }),
    // text/csv (live) with a JSON acknowledgement fallback (mock). The UI treats
    // a non-empty string as a successful (signed-url-redacted) export.
    exportCsv: (exportType: string, query?: Query): Promise<string> => requestContractText(requester, exportType, query)
  };
}
